import { GraphQLOptions, GraphQLResult } from '@aws-amplify/api-graphql';
import { generateClient } from 'aws-amplify/api';
import * as Sentry from '@sentry/react';
import {
  sendApplicationError,
  sendException,
  FINGERPRINT_APPLICATION_ERROR,
  FINGERPRINT_SYSTEM_ERROR,
  createCustomError
} from '@/lib/sentryClient';
import { Result } from '@/lib/axiosClient';
import { GraphQLError } from 'graphql/error/GraphQLError';

const client = generateClient();

export const executeQuery = async <T>(
  query: string,
  variables?: Record<string, any>,
  authMode?: GraphQLOptions['authMode']
): Promise<Result<T>> => {
  const operation = getOperationName(query);
  const operationName = operation?.operationName ?? 'unknown Operation';
  try {
    const res = (await client.graphql({
      query,
      variables,
      authMode
    })) as GraphQLResult<any>;
    if (res.errors && res.errors.length > 0) {
      const error = createCustomError(res.errors[0]);
      error.name = `${operationName} ${error.name}`;
      error.message = `failed to ${operationName} ${FINGERPRINT_APPLICATION_ERROR} : ${error.message}`;
      const extra = {
        extra: {
          Query: {
            operationName: operationName,
            request: JSON.stringify(variables),
            operation: operation?.operation ?? 'unknown',
            response: JSON.stringify(res.data),
            extentions: JSON.stringify(res.extensions)
          }
        }
      };

      if (res.errors.length > 1) {
        sendErrorToBreadClumbs(res.errors.slice(1));
      }

      sendApplicationError(error, operationName, extra);
      return {
        result: false,
        error: error
      };
    }

    Sentry.addBreadcrumb({
      category: 'GraphQL',
      message: `${operationName} executed`,
      data: {
        request: JSON.stringify(variables),
        response: JSON.stringify(res.data)
      }
    });

    return {
      result: true,
      data: res.data
    };
  } catch (e) {
    const error = createCustomError(new Error(`failed to ${operationName} ${FINGERPRINT_SYSTEM_ERROR}`));
    if (e instanceof Error) {
      error.message = `${error.message}: ${e.message}`;
      error.stack = e.stack;
    }
    error.name = `${operationName} ${error.name}`;
    const extra = {
      Query: {
        operationName: operationName,
        request: JSON.stringify(variables),
        operation: operation?.operation ?? 'unknown'
      }
    };
    sendException(error, operationName, extra);
    return {
      result: false,
      error: error
    };
  }
};

type Operation = {
  operation: string;
  operationName: string;
};
// 操作名を取得する関数
const getOperationName = (query: string): Operation | null => {
  const match = query.match(/(query|mutation|subscription)\s+(\w+)/);
  if (match && match[2]) {
    return {
      operation: match[1],
      operationName: match[2]
    };
  }
  return null;
};

// GraphQLから返却されるエラーの中で先頭以外のエラーをパンくずリストに出力する
const sendErrorToBreadClumbs = (errors: GraphQLError[]) => {
  errors.forEach((error: GraphQLError) => {
    Sentry.addBreadcrumb({
      category: 'GraphQL.Error',
      message: error.message,
      data: {
        name: error.name,
        path: error.path,
        extentions: JSON.stringify(error.extensions)
      }
    });
  });
};
